import { initContract } from '@ts-rest/core'
import { z } from 'zod'

const c = initContract()

export const dashboardContract = c.router({
  getReachedRate: c.query({
    method: 'GET',
    path: '/v2/dashboard/reached-rate',
    query: z.object({
      customerId: z.string().optional(),
      campaignIds: z.preprocess((val) => {
        if (Array.isArray(val)) return val
        if (typeof val === 'string') return val.split(',').filter(Boolean)
        if (val && typeof val === 'object') return Object.values(val)
        return undefined
      }, z.array(z.string()).optional()),
      campaignGroupId: z.string().optional(),
      startDate: z.number(),
      endDate: z.number(),
    }),
    responses: {
      200: z.object({
        reachedAudience: z.number(),
        totalAudience: z.number(),
        reachedPercentage: z.number(),
      }),
      500: z.object({
        error: z.string(),
      }),
    },
    summary: 'Get the reached rate',
  }),

  getAverageHappinessCheck: c.query({
    method: 'GET',
    path: '/v2/dashboard/average-happiness-check',
    query: z.object({
      customerId: z.string().optional(),
      campaignIds: z.preprocess((val) => {
        if (Array.isArray(val)) return val
        if (typeof val === 'string') return val.split(',').filter(Boolean)
        if (val && typeof val === 'object') return Object.values(val)
        return undefined
      }, z.array(z.string()).optional()),
      campaignGroupId: z.string().optional(),
      startDate: z.number(),
      endDate: z.number(),
    }),
    responses: {
      200: z.object({
        averageHappiness: z.number(),
        totalResponses: z.number(),
        totalAudience: z.number(),
      }),
      500: z.object({
        error: z.string(),
      }),
    },
    summary: 'Get the average happiness check',
  }),

  getIncentiveClaimRate: c.query({
    method: 'GET',
    path: '/v2/dashboard/incentive-claim-rate',
    query: z.object({
      customerId: z.string().optional(),
      campaignIds: z.preprocess((val) => {
        if (Array.isArray(val)) return val
        if (typeof val === 'string') return val.split(',').filter(Boolean)
        if (val && typeof val === 'object') return Object.values(val)
        return undefined
      }, z.array(z.string()).optional()),
      campaignGroupId: z.string().optional(),
      startDate: z.number(),
      endDate: z.number(),
    }),
    responses: {
      200: z.object({
        claimedIncentives: z.number(),
        totalAudience: z.number(),
        claimRate: z.number(),
      }),
      500: z.object({
        error: z.string(),
      }),
    },
    summary: 'Get the incentive claim rate',
  }),

  getQuestionnaireCompletionRate: c.query({
    method: 'GET',
    path: '/v2/dashboard/questionnaire-completion-rate',
    query: z.object({
      customerId: z.string().optional(),
      campaignIds: z.preprocess((val) => {
        if (Array.isArray(val)) return val
        if (typeof val === 'string') return val.split(',').filter(Boolean)
        if (val && typeof val === 'object') return Object.values(val)
        return undefined
      }, z.array(z.string()).optional()),
      campaignGroupId: z.string().optional(),
      startDate: z.number(),
      endDate: z.number(),
    }),
    responses: {
      200: z.object({
        totalUsers: z.number(),
        completionRate: z.number(),
        totalAnsweredQuestions: z.number(),
        totalQuestions: z.number(),
      }),
      500: z.object({
        error: z.string(),
      }),
    },
    summary: 'Get the questionnaire completion rate',
  }),
  getCampaignQuestions: c.query({
    method: 'GET',
    path: '/v2/dashboard/campaign-questions',
    query: z.object({
      page: z.union([z.string(), z.number()]).optional().default('1'),
      limit: z.union([z.string(), z.number()]).optional().default('10'),
      periodStart: z.string(),
      periodEnd: z.string(),
      customer_id: z.string().optional(),
      campaignIds: z.preprocess((val) => {
        if (Array.isArray(val)) return val
        if (typeof val === 'string') return val.split(',').filter(Boolean)
        if (val && typeof val === 'object') return Object.values(val)
        return undefined
      }, z.array(z.string()).optional()),
      campaignGroupId: z.string().optional(),
      questionContext: z.enum(['all', 'standard', 'referral']).default('all'),
    }),
    responses: {
      200: z.object({
        data: z.array(
          z.object({
            question_id: z.string(),
            question_name: z.string(),
            question_text: z.string(),
            question_type: z.string(),
            campaigns: z.array(
              z.object({
                campaign_id: z.string(),
                campaign_name: z.string(),
                campaign_status: z.string(),
                customer_id: z.string(),
                customer_name: z.string(),
              }),
            ),
          }),
        ),
        totalCount: z.number(),
        page: z.number(),
        limit: z.number(),
        totalPages: z.number(),
      }),
      500: z.object({
        error: z.string(),
      }),
    },
    summary: 'Get all questions from campaigns grouped by question_id',
  }),
  getCustomers: c.query({
    method: 'GET',
    path: '/v2/dashboard/customers',
    query: z.object({}),
    responses: {
      200: z.array(
        z.object({
          id: z.string(),
          name: z.string(),
        }),
      ),
      500: z.object({
        error: z.string(),
      }),
    },
    summary: 'Get all customers (superadmin only)',
  }),
  getCampaignGroups: c.query({
    method: 'GET',
    path: '/v2/dashboard/campaign-groups',
    query: z.object({
      customerId: z.string().optional(),
    }),
    responses: {
      200: z.object({
        campaignGroups: z.array(
          z.object({
            id: z.string(),
            name: z.string(),
            description: z.string().optional(),
            campaignIds: z.array(z.string()),
          }),
        ),
        ungroupedCampaigns: z.array(
          z.object({
            id: z.string(),
            name: z.string(),
          }),
        ),
      }),
      500: z.object({
        error: z.string(),
      }),
    },
    summary: 'Get campaign groups and ungrouped campaigns',
  }),
  getCampaignSummary: c.query({
    method: 'GET',
    path: '/v2/dashboard/campaign-summary',
    query: z.object({
      periodStart: z.string(),
      periodEnd: z.string(),
      customer_id: z.string().optional(),
      campaignIds: z.string().optional(), // Comma-separated campaign IDs to filter
    }),
    responses: {
      200: z.object({
        totalCampaigns: z.number(),
        avgCompletionRate: z.number(),
        totalUsers: z.number(),
      }),
      500: z.object({
        error: z.string(),
      }),
    },
    summary: 'Get campaign summary statistics',
  }),

  getQuestionResponses: c.query({
    method: 'GET',
    path: '/v2/dashboard/question-responses',
    query: z.object({
      campaignId: z.string(),
      questionName: z.string(),
      periodStart: z.string(),
      periodEnd: z.string(),
      customerId: z.string().optional(),
    }),
    responses: {
      200: z.object({
        questionText: z.string(),
        campaignName: z.string(),
        answers: z.array(
          z.object({
            answer: z.string(),
            count: z.number(),
            percentage: z.number(),
          }),
        ),
        totalResponses: z.number(),
      }),
      500: z.object({
        error: z.string(),
      }),
    },
    summary: 'Get responses for a specific question in a campaign',
  }),

  getCampaignQuestionsWithAnswers: c.query({
    method: 'GET',
    path: '/v2/dashboard/campaigns/:campaignId/questions-with-answers',
    pathParams: z.object({
      campaignId: z.string(),
    }),
    query: z.object({
      aggregatesOnly: z.boolean().optional().default(false),
      questionIds: z.string().optional(), // Comma-separated question IDs
      questionContext: z
        .enum(['all', 'standard', 'referral'])
        .optional()
        .default('all'),
    }),
    responses: {
      200: z.object({
        campaignId: z.string(),
        title: z.string(),
        totalParticipants: z.number(),
        questions: z.array(
          z.object({
            questionId: z.string(),
            type: z.enum([
              'short_text',
              'long_text',
              'email',
              'dropdown',
              'date',
              'picture_choice',
              'dynamic_radio_multi',
              'gender_radio',
              'checkbox',
            ]),
            label: z.string(),
            options: z
              .array(
                z.object({
                  value: z.string(),
                  label: z.string(),
                }),
              )
              .optional(),
            answers: z.array(
              z.union([
                // Short/Long text
                z.object({
                  value: z.string(),
                  submittedAt: z.string(),
                }),
                // Email
                z.object({
                  value: z.string(),
                  submittedAt: z.string(),
                }),
                // Dropdown/Picture/Gender
                z.object({
                  value: z.string(),
                }),
                // Multi-select
                z.object({
                  value: z.array(z.string()),
                }),
                // Date
                z.object({
                  value: z.string(), // ISO 8601 date
                }),
                // Checkbox
                z.object({
                  value: z.boolean(),
                }),
              ]),
            ),
            aggregates: z
              .object({
                buckets: z.array(
                  z.object({
                    label: z.string(),
                    value: z.string().optional(),
                    count: z.number(),
                  }),
                ),
                unknownCount: z.number().optional(),
              })
              .optional(),
            totalRespondents: z.number().optional(),
          }),
        ),
      }),
      500: z.object({
        error: z.string(),
      }),
    },
    summary: 'Get all questions and answers for a specific campaign',
  }),

  getGroupedQuestionsWithAnswers: c.query({
    method: 'GET',
    path: '/v2/dashboard/grouped-questions-with-answers',
    query: z.object({
      campaignGroupId: z.string(),
      questionIds: z.string().optional(),
      questionContext: z
        .enum(['all', 'standard', 'referral'])
        .optional()
        .default('all'),
    }),
    responses: {
      200: z.object({
        campaignId: z.string(),
        title: z.string(),
        totalParticipants: z.number(),
        questions: z.array(
          z.object({
            questionId: z.string(),
            type: z.enum([
              'short_text',
              'long_text',
              'email',
              'dropdown',
              'date',
              'picture_choice',
              'dynamic_radio_multi',
              'gender_radio',
              'checkbox',
            ]),
            label: z.string(),
            options: z
              .array(
                z.object({
                  value: z.string(),
                  label: z.string(),
                }),
              )
              .optional(),
            answers: z.array(
              z.union([
                z.object({
                  value: z.string(),
                  submittedAt: z.string(),
                }),
                z.object({
                  value: z.string(),
                  submittedAt: z.string(),
                }),
                z.object({
                  value: z.string(),
                }),
                z.object({
                  value: z.array(z.string()),
                }),
                z.object({
                  value: z.string(),
                }),
                z.object({
                  value: z.boolean(),
                }),
              ]),
            ),
            aggregates: z
              .object({
                buckets: z.array(
                  z.object({
                    label: z.string(),
                    value: z.string().optional(),
                    count: z.number(),
                  }),
                ),
                unknownCount: z.number().optional(),
              })
              .optional(),
            totalRespondents: z.number().optional(),
          }),
        ),
      }),
      500: z.object({
        error: z.string(),
      }),
    },
    summary:
      'Get aggregated questions with answers across all campaigns in a group',
  }),

  getRiskFactors: c.query({
    method: 'GET',
    path: '/v2/dashboard/risk-factors',
    query: z.object({
      startDate: z.number(),
      endDate: z.number(),
      customerId: z.string().optional(),
      campaignIds: z.preprocess((val) => {
        if (Array.isArray(val)) return val
        if (typeof val === 'string') return val.split(',').filter(Boolean)
        if (val && typeof val === 'object') return Object.values(val)
        return undefined
      }, z.array(z.string()).optional()),
      campaignGroupId: z.string().optional(),
    }),
    responses: {
      200: z.object({
        totalUsers: z.number(),
        riskGroups: z.object({
          neverOpened: z.object({
            count: z.number(),
            percentage: z.number(),
            description: z.string(),
          }),
          lowHappiness: z.object({
            count: z.number(),
            percentage: z.number(),
            description: z.string(),
          }),
          nonResponders: z.object({
            count: z.number(),
            percentage: z.number(),
            description: z.string(),
          }),
        }),
        summary: z.object({
          totalAtRisk: z.number(),
          riskPercentage: z.number(),
        }),
      }),
      500: z.object({
        error: z.string(),
      }),
    },
    summary: 'Get risk factors analysis for end users',
  }),

  exportRiskFactorsCsv: c.query({
    method: 'GET',
    path: '/v2/dashboard/risk-factors/export',
    query: z.object({
      startDate: z.number(),
      endDate: z.number(),
      customerId: z.string().optional(),
      riskTypes: z.string().optional(), // comma-separated: neverOpened,lowHappiness,nonResponders
    }),
    responses: {
      200: z.string(), // CSV content
      500: z.object({
        error: z.string(),
      }),
    },
    summary: 'Export risk factors data as CSV',
  }),

  getTopIncentives: c.query({
    method: 'GET',
    path: '/v2/dashboard/top-incentives',
    query: z.object({
      startDate: z.number(),
      endDate: z.number(),
      customerId: z.string().optional(),
      campaignIds: z.preprocess((val) => {
        if (Array.isArray(val)) return val
        if (typeof val === 'string') return val.split(',').filter(Boolean)
        if (val && typeof val === 'object') return Object.values(val)
        return undefined
      }, z.array(z.string()).optional()),
      campaignGroupId: z.string().optional(),
    }),
    responses: {
      200: z.array(
        z.object({
          incentiveId: z.string(),
          incentiveName: z.string(),
          incentiveType: z.string(),
          selectionCount: z.number(),
          totalUsers: z.number(),
          selectionPercentage: z.number(),
          totalVouchers: z.number(),
          usedVouchers: z.number(),
          freeVouchers: z.number(),
        }),
      ),
      500: z.object({
        error: z.string(),
      }),
    },
    summary:
      'Get top 6 most selected incentives with percentages and voucher statistics',
  }),

  getChosenGifts: c.query({
    method: 'GET',
    path: '/v2/dashboard/chosen-gifts',
    query: z.object({
      startDate: z.number(),
      endDate: z.number(),
      customerId: z.string().optional(),
      campaignIds: z.preprocess((val) => {
        if (Array.isArray(val)) return val
        if (typeof val === 'string') return val.split(',').filter(Boolean)
        if (val && typeof val === 'object') return Object.values(val)
        return undefined
      }, z.array(z.string()).optional()),
      campaignGroupId: z.string().optional(),
    }),
    responses: {
      200: z.array(
        z.object({
          parentIncentiveItemId: z.string(),
          parentIncentiveName: z.string(),
          giftIncentiveItemId: z.string(),
          giftIncentiveName: z.string(),
          giftType: z.string(),
          timesChosen: z.number(),
          sharePercentage: z.number(),
        }),
      ),
      500: z.object({
        error: z.string(),
      }),
    },
    summary:
      'Get the gifts recipients chose inside each gift group in the gift portal',
  }),

  getEngagementMetrics: c.query({
    method: 'GET',
    path: '/v2/dashboard/engagement-metrics',
    query: z.object({
      customerId: z.string().optional(),
      campaignIds: z.preprocess((val) => {
        if (Array.isArray(val)) return val
        if (typeof val === 'string') return val.split(',').filter(Boolean)
        if (val && typeof val === 'object') return Object.values(val)
        return undefined
      }, z.array(z.string()).optional()),
      campaignGroupId: z.string().optional(),
      startDate: z.number(),
      endDate: z.number(),
    }),
    responses: {
      200: z.object({
        qrEmailOpens: z.number(),
        giftSelection: z.number(),
        questionsAnswered: z.number(),
        claimedGift: z.number(),
        ratingsGiven: z.number(),
        sharedReferrals: z.number(),
        quickResponse: z.number(),
        totalAudience: z.number(),
        qrEmailOpensPercentage: z.number(),
        giftSelectionPercentage: z.number(),
        questionsAnsweredPercentage: z.number(),
        claimedGiftPercentage: z.number(),
        ratingsGivenPercentage: z.number(),
        sharedReferralsPercentage: z.number(),
        quickResponsePercentage: z.number(),
      }),
      500: z.object({
        error: z.string(),
      }),
    },
    summary: 'Get engagement metrics for campaigns',
  }),
})

// Export the contract type for client usage
export type DashboardContract = typeof dashboardContract
